import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Card, CardContent, Divider, Grid, Button } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useYupValidationResolver } from 'hooks/yupValidation/useYupValidationResolver';
import * as yup from 'yup';
import { TextInput, Dropdown, Autocomplete } from 'qubCommon/Inputs';
import ImagePicker from 'ui-component/image_picker/ImagePicker';
import { ProjectTables } from 'enums';
import { membership } from 'apis';
import { AttachEmail, Attachment, ContactEmergency, Contacts, Groups } from '@mui/icons-material';
import { getMunicipalities } from 'apis/controllers/person';
import { useQuery } from 'react-query';
import { debounce } from 'lodash';
import { useParams } from 'react-router';
import { getLookups } from 'apis/controllers/backoffice';
import { useSelector } from 'react-redux';
import ClubFiles from './ClubFiles';

function validateQuestionnaires(data) {
    return data.every((obj) =>
        obj.answers.every(
            (answerObj) =>
                Array.isArray(answerObj.answer) && answerObj.answer.filter((item) => item !== null && item !== undefined).length > 0
        )
    );
}

const ClubProfileForm = ({ clubQuery, agreementsQuery, mode }) => {
    const { t } = useTranslation();
    const formRef = useRef();
    const { id } = useParams();
    const { projectCountryId } = useSelector((state) => state.auth);
    const fileRefs = useRef([]);

    const schema = yup.object().shape({
        title: yup.string().required('Club Title is required'),
        abbreviation: yup.string().required('Abbreviation is required'),
        fullName: yup.string(),
        parentOrganizationId: yup.string(),
        entityType: yup.string(),
        email: yup.string().email('Invalid email'),
        phone1: yup.string(),
        phone2: yup.string(),
        fax: yup.string(),
        website: yup.string(),
        countryId: yup.string(),
        street: yup.string(),
        city: yup.string(),
        postalCode: yup.string(),
        province: yup.string()
    });

    const resolver = useYupValidationResolver(schema);

    const methods = useForm({
        resolver: resolver,
        defaultValues: {
            ...clubQuery?.data?.data,

            geolocation: clubQuery?.data?.data?.geoLocationId
                ? {
                      id: clubQuery?.data?.data?.geoLocationId,
                      name: clubQuery?.data?.data?.geolocationTitle
                  }
                : null
        }
    });

    const [municipalityValue, setMunicipalityValue] = useState('');

    const municipalitiesQuery = useQuery({
        queryKey: ['municipalities', municipalityValue],
        queryFn: async () => {
            var res = await getMunicipalities({
                search: municipalityValue
            });
            return res;
        }
    });

    const countriesQuery = useQuery('countries', async () => {
        const res = await getLookups({ dataKey: 'Countries' });
        return res;
    });

    const handleMunicipalitiesAutoCompleteChange = debounce(function (value) {
        setMunicipalityValue(value);
    }, 500);

    const onSubmit = async (data) => {
        /* Below is the code to prevent save if questionnaire is not filled */

        // const completeQuestionnaire = validateQuestionnaires(data?.questionnaires?.filter(Boolean).flat());

        // if (!completeQuestionnaire) {
        //     setQuestionnaireError(true);
        //     return;
        // } else setQuestionnaireError(false);

        if (fileRefs.current) {
            await Promise.all(
                fileRefs.current.map(async (childRef) => {
                    if (childRef && childRef.filebundles?.files?.filter((file) => file?.id)?.map((file) => file.id)?.length > 0) {
                        await childRef.uploadFiles();
                    }
                })
            );
        }
        const files = [];
        fileRefs.current.forEach((childRef) => {
            if (childRef) {
                // childRef.saveData();
                if (childRef.filebundles?.files?.filter((file) => file?.id)?.map((file) => file.id)?.length > 0)
                    files.push({
                        id: childRef.filebundles.id,
                        bundles: childRef.filebundles?.files?.filter((file) => file?.id)?.map((file) => file.id) || []
                    });
            }
        });

        const funcMappedData = {
            ...data,
            geoLocationId: data?.geolocation?.id ?? null
        };

        const agreements = data?.agreements;

        const formData = await formatForSave(
            funcMappedData,
            null,
            null,
            [],
            files,
            data?.questionnaires?.filter(Boolean).flat(),
            agreements
        );
        /* below save apis */

        // if (mode === 'edit') {
        //     try {
        //         const res = await updateProfile(formData);
        //         if (res?.status === 'success') {
        //             enqueueSnackbar(res?.message, { variant: 'success' });
        //             queryClient.invalidateQueries('questionnaire');
        //             navigate('/home');
        //         }
        //         if (res?.status !== 'success') enqueueSnackbar(res?.message, { variant: res?.status });
        //     } catch (e) {
        //         console.log(e);
        //         enqueueSnackbar('Oops, something went wrong...', { variant: 'error' });
        //     } finally {
        //         queryClient.invalidateQueries('profile');
        //     }
        // }

        // if (mode === 'create') {
        //     try {
        //         const res = await createMember(formData);
        //         if (res?.status === 'success') {
        //             enqueueSnackbar(res?.message, { variant: 'success' });
        //             queryClient.invalidateQueries('questionnaire');
        //             navigate('/home');
        //         }
        //         if (res?.status !== 'success') enqueueSnackbar(res?.message, { variant: res?.status });
        //     } catch (e) {
        //         console.log(e);
        //         enqueueSnackbar('Oops, something went wrong...', { variant: 'error' });
        //     } finally {
        //         queryClient.invalidateQueries('profile');
        //     }
        // }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Box sx={{ maxWidth: 900, mx: 'auto', p: 4 }}>
                    {/* Basic Info */}

                    <Grid item xs={12}>
                        <Typography variant="h2" fontWeight="bold" align="center">
                            {'Club Profile'}
                        </Typography>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Card elevation={3} sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Groups sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                                <Typography variant="h4" fontWeight="bold">
                                    {t('ClubInfo')}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                {/* Avatar */}
                                <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                                    <ImagePicker
                                        api={membership}
                                        ProjectTable={ProjectTables.Organization}
                                        recordId={null}
                                        recordGuid={id}
                                        aspect={1}
                                        editMode
                                        showPairStatus={false}
                                    />
                                </Grid>

                                {/* Club Details */}
                                <Grid item xs={12} md={9}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextInput name="title" label="Club Title" fullWidth />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextInput name="abbreviation" label="Abbreviation" fullWidth />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextInput name="fullName" label="Full Formal Name" fullWidth />
                                        </Grid>
                                        {/* <Grid item xs={12} md={6}>
                                            <Dropdown name="parentOrganizationId" label="Parent Organization" options={[]} fullWidth />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Dropdown
                                                name="entityType"
                                                label="Entity Type"
                                                options={[{ label: 'Club', value: 'Club' }]}
                                                fullWidth
                                            />
                                        </Grid> */}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Location */}

                    <Card elevation={3} sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Groups sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                                <Typography variant="h4" fontWeight="bold">
                                    {t('LocationInfo')}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Dropdown name="countryId" label="Country" options={countriesQuery?.data} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    {methods.watch('countryId') === projectCountryId && (
                                        <Autocomplete
                                            label={t('Municipality')}
                                            name="geolocation"
                                            labelName="name"
                                            options={municipalitiesQuery?.data || []}
                                            onInputChange={(_, value) => handleMunicipalitiesAutoCompleteChange(value)}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextInput label={t('Number')} fullWidth name="streetNr" defaultFromProps={false} />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextInput label={t('Street')} fullWidth name="address1" defaultFromProps={false} />
                                </Grid>

                                <Grid item xs={12} sm={12}>
                                    <TextInput label={t('Address2')} fullWidth name="address2" defaultFromProps={false} />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextInput label={t('ZipCode')} fullWidth name="zipCode" defaultFromProps={false} />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <TextInput label={t('Region')} fullWidth name="region" defaultFromProps={false} />
                                </Grid>

                                <Grid item xs={12} sm={5}>
                                    <TextInput label={t('City')} fullWidth name="city" defaultFromProps={false} />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <ClubFiles ref={(el) => (fileRefs.current[0] = el)} />

                    <Card elevation={3} sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Contacts sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                                <Typography variant="h4" fontWeight="bold">
                                    {t('ContactInfo')}
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextInput name="email" label="Email" fullWidth />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextInput name="phone1" label="Phone 1" fullWidth />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextInput name="phone2" label="Phone 2" fullWidth />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextInput name="fax" label="Fax" fullWidth />
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <TextInput name="website" label="Website" fullWidth />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Comments */}
                    <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                        {t('Etc')}
                    </Typography>
                    <Card elevation={3} sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextInput name="internal" label="Internals" fullWidth multiline rows={2} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextInput name="adminComment" label="Admin Comments" fullWidth multiline rows={2} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextInput name="comment" label="Comments" fullWidth multiline rows={2} />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Submit */}
                    <Button type="submit" fullWidth size="large" variant="contained" color="primary">
                        Save Club Profile
                    </Button>
                </Box>
            </form>
        </FormProvider>
    );
};

export default ClubProfileForm;
